import { Component, ViewChild } from '@angular/core';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { GuiUtils } from 'src/modules/utils/misc/gui-utils';
import { TreeNode2 } from 'src/modules/utils/misc/tree-node2.model';
import { DhTools } from '../../models/dh-tools.model';
import { MenuItem } from 'primeng/api';
import { ConfigEditorTreeComponent } from 'src/modules/sm-base/components/config-editor-tree/config-editor-tree.component';
import { FrontendFieldDefinition } from 'src/modules/sm-base/models/frontend-field-definition.model';
import { FrontendFieldType } from 'src/modules/sm-base/models/frontend-field-type.enum';
import { OrdinaryObject } from 'src/modules/utils/shared/ordinary-object.model';
import { Utils } from 'src/modules/utils/shared/utils';
import { ConfigFieldDefinition } from 'src/modules/sm-base/models/config-field-definition.model';
import { ConfigFieldType } from 'src/modules/sm-base/models/config-field-type.enum';
import { ConfigProperty } from 'src/modules/sm-base/models/config-property.model';
import { ConfigDefinition } from 'src/modules/sm-base/models/config-definition.model';
import { AiAdapterDefinition } from 'src/modules/enaio/shared/AiAdapterDefinition';
import { FrontendFormDefinition } from 'src/modules/sm-base/models/frontend-form-definition.model';
import { FrontendFieldAddon } from 'src/modules/sm-base/models/frontend-field-addon.model';
import { EnaioObjectDef } from 'src/modules/enaio/shared/EnaioObjectDef';
import { EnaioCallGetObjectDef } from 'src/modules/enaio/shared/EnaioCallGetObjectDef';
import { FrontendFieldListItem } from 'src/modules/sm-base/models/frontend-field-list-item.model';
import { FrontendEnaioMapper } from 'src/modules/enaio/models/frontend-enaio-mapper.model';
import { EnaioObjectType } from 'src/modules/enaio/shared/EnaioObjectType';

@Component({
  selector: 'app-text-extractor',
  templateUrl: './text-extractor.component.html',
  styleUrl: './text-extractor.component.scss'
})
export class TextExtractorComponent extends ComponentView {

    items: AiAdapterDefinition[];
    def: ConfigDefinition;
    config: ConfigProperty;
    documentText: string;
    documentTextWithLayout: string;
    evaluationTree: TreeNode2[];
    evaluationText: string;
    resultSchema: string;
    menuItems: MenuItem[] = [];
    form: FrontendFormDefinition;
    objectDef: EnaioObjectDef;
    enaioForm: FrontendFormDefinition;

    @ViewChild("configEditorTree") configEditorTree: ConfigEditorTreeComponent;
    @ViewChild('viewer') viewer;

    async initParams(): Promise<boolean> {        
        this.objectDef = await DhTools.enaioCall<EnaioObjectDef>(new EnaioCallGetObjectDef());

        let otds = Utils.arrayExplode(this.objectDef.cabinets, c => c.objectTypes);

        this.def = await DhTools.backendCall("api/config/definition").get(ConfigDefinition);
        this.def.init();
        this.configEditorTree.def = this.def;

        let plain = {};

        let mainFieldDef = Utils.fromPlain(ConfigFieldDefinition, { name: "main", type: ConfigFieldType.object, typeName: "CsUtils.AiAdapterDefinition"});
        this.config = new ConfigProperty({}, "CsUtils.AiAdapterDefinition", true, mainFieldDef, [], plain);
        let d = this.def.getByName("CsUtils.AiAdapterDefinition");
        this.config.properties = mainFieldDef.getProperties(d, plain, this.def);

        this.configEditorTree.setObject(this.config);

        await this.updateItems();
        this.fillConnection(this.items[0]);

        this.form = new FrontendFormDefinition([
            new FrontendFieldDefinition("fileName", "Dokumentdatei", FrontendFieldType.text, { 
                onAddOnClick: this.selectFile.bind(this),
                addOnButtons: [new FrontendFieldAddon("open", "", "fas fa-upload", true)] }),
            new FrontendFieldDefinition("ocr", "OCR anwenden", FrontendFieldType.checkBox, { value: true }),                        
            new FrontendFieldDefinition("useLayoutText", "Eingabetext mit Layout verwenden", FrontendFieldType.checkBox, { value: true }),                        
            new FrontendFieldDefinition("markTextInDocument", "Gefundenen Text im Dokument markieren", FrontendFieldType.checkBox),                        
            new FrontendFieldDefinition("markSignaturesInDocument", "Gefundene Unterschriften im Dokument markieren", FrontendFieldType.checkBox),                        
            new FrontendFieldDefinition("inputText", "oder Dokumenttext", FrontendFieldType.textArea),
            new FrontendFieldDefinition("schema", "JSON-Schema für Ergebnis", FrontendFieldType.textArea, { monospaced: true }),
            new FrontendFieldDefinition("recognitionHints", "Hinweise für Erkennung", FrontendFieldType.textArea),
            new FrontendFieldDefinition("returnSchema", "Schema ermitteln", FrontendFieldType.checkBox),
            new FrontendFieldDefinition("fillObjectType", "enaio Objekttyp befüllen", FrontendFieldType.comboBox, { dropdownEditable: false, 
                listItems: [new FrontendFieldListItem(null, ""), ...Utils.arraySort(Utils.arrayExplode(this.objectDef.cabinets, c => c.objectTypes.map(ot => new FrontendFieldListItem(ot, c.name + " -> " + ot.name))))]})
        ]);

        return true;
    }

    async selectFile(): Promise<void> {
        this.form.setValue("fileName", await DhTools.openDialog());
    }

    async updateItems(): Promise<void> {
        this.items = await DhTools.backendCall("api/enaio/aiGetDefaultAdapters").list(AiAdapterDefinition);
        this.menuItems = [
        {
            label: "Aus Clipboard",
            command: async () => {
                await this.loadAdapter(true);
            }
        },
        {
            label: "Eingabe ...",
            command: async () => {
                await this.loadAdapter();
            }
        },
        ...this.items.map(item => ({
            label: item.type + " - " + item.model,
            command: () => {
                this.fillConnection(item);
            }
        }))];
    }

    async loadAdapter(useClip = false): Promise<void> {
        let s = "";
        if (useClip) {
            s = await GuiUtils.fromClipboard();
        }
        else {
            s = await this.app.messageDialog.input(new FrontendFieldDefinition("", "Definition", FrontendFieldType.textArea, { value: GuiUtils.isClipboardReadSupported() ? await GuiUtils.fromClipboard() : ""}), "Adapterdefinition im DocumentHandler-JSON-Format");
            if (s == null) {
                return;
            }
        }
        this.fillConnection(Utils.fromPlainUnsafe(AiAdapterDefinition, Utils.fromJson(s)));
    }

    fillConnection(connection: AiAdapterDefinition): void {
        let mainFieldDef = Utils.fromPlain(ConfigFieldDefinition, { name: "main", type: ConfigFieldType.object, typeName: "CsUtils.AiAdapterDefinition"});
        this.config = new ConfigProperty({}, "CsUtils.AiAdapterDefinition", true, mainFieldDef, [], connection);
        let d = this.def.getByName("CsUtils.AiAdapterDefinition");
        this.config.properties = mainFieldDef.getProperties(d, connection, this.def);

        this.configEditorTree.setObject(this.config);
    }

    async extractInformation(): Promise<void> {
        let returnSchema = this.form.getValue("returnSchema") as boolean;
        let fillObjectType = this.form.getValue("fillObjectType") as EnaioObjectType;
        let data = await DhTools.backendCall("api/enaio/aiExtractInformation", { 
            fileName: this.form.getValue("fileName"),
            ocr: this.form.getValue("ocr"),
            useLayoutText: this.form.getValue("useLayoutText"),
            markTextInDocument: this.form.getValue("markTextInDocument"),
            markSignaturesInDocument: this.form.getValue("markSignaturesInDocument"),
            inputText: this.form.getValue("inputText"), 
            schema: this.form.getValue("schema"),
            recognitionHints: this.form.getValue("recognitionHints"),
            returnDocumentContents: !Utils.isNoe(this.form.getValue("fileName")),
            returnSchema: returnSchema,
            fillObjectType: fillObjectType?.internalName,
            adapter: this.configEditorTree.getObject() 
        }).getOrdinaryObject(Object);

        this.evaluationTree = GuiUtils.objectToTree(data.result, true);
        this.evaluationText = Utils.toJson(data.result, true);
        this.documentText = data.text as string;
        this.documentTextWithLayout = data.textWithLayout as string;
        this.resultSchema = returnSchema ? data.resultSchema as string : "Das Schema wurde nicht ermittelt";
        for (let node of GuiUtils.flattenTree(this.evaluationTree)) {
            node.expanded = true;
        }
        if (fillObjectType != null) {
            this.enaioForm = FrontendEnaioMapper.enaioToFrontendForm(fillObjectType.getAllFields(), this.app, "", [], true);
            this.enaioForm.mandatoryFieldsRedAsterisk = false;
            for (let fieldName of Utils.getOwnPropertyNames(data.result)) {
                let of = fillObjectType.getAllFields().find(f => f.name == fieldName);
                if (of != null) {
                    let field = this.enaioForm.getField(of.internalName, false);
                    if (field != null) {
                        field.setValue(data.result[fieldName]);
                    }
                }
            }
        }
        else {
            this.enaioForm = null;
        }
        this.viewer.pdfSrc = data.documentBase64 != null ? GuiUtils.base64ToBlob(data.documentBase64 as string, "application/pdf") : null;
        this.viewer.refresh();
    }

    

}
